import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Container,
  Button,
  HStack,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  useDisclosure,
  Image,
  Text,
  Divider,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { HamburgerIcon } from '@chakra-ui/icons';
import { Building2, Workflow, TrendingUp, Store, MessageSquare, ArrowUpRight, Mail, Phone } from 'lucide-react';

const NAV_LINKS = [
  { label: 'About Us', to: '/about', icon: Building2 },
  { label: 'How It Works', to: '/how-it-works', icon: Workflow },
  { label: 'Investments', to: '/investments', icon: TrendingUp },
  { label: 'Marketplace', to: '/marketplace', icon: Store },
  { label: 'Contact', to: '/contact', icon: MessageSquare },
];

const NavLink = ({ to, isActive, children }) => (
  <Box
    as={RouterLink}
    to={to}
    color={isActive ? '#D4A85F' : '#D6DCE5'}
    fontFamily="body"
    fontWeight={isActive ? 600 : 500}
    fontSize="sm"
    letterSpacing="0.01em"
    transition="color 0.2s ease"
    position="relative"
    _hover={{ color: '#D4A85F' }}
    _after={isActive ? {
      content: '""',
      position: 'absolute',
      bottom: '-6px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '20px',
      height: '2px',
      bg: 'linear-gradient(90deg, transparent 0%, #D4A85F 50%, transparent 100%)',
      borderRadius: 'full',
    } : {}}
  >
    {children}
  </Box>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  return (
    <Box
      as="header"
      position="fixed"
      top={scrolled ? { base: '8px', md: '12px' } : 0}
      left={scrolled ? { base: '16px', md: '64px', lg: '120px' } : 0}
      right={scrolled ? { base: '16px', md: '64px', lg: '120px' } : 0}
      zIndex={1000}
      bg={scrolled ? 'rgba(7,19,38,0.82)' : '#071326'}
      backdropFilter={scrolled ? 'saturate(140%) blur(14px)' : 'none'}
      borderRadius={scrolled ? '12px' : '0'}
      border="1px solid"
      borderColor={scrolled ? 'rgba(255,255,255,0.10)' : 'transparent'}
      boxShadow={scrolled ? '0 12px 32px rgba(0,0,0,0.32), 0 2px 8px rgba(0,0,0,0.20)' : 'none'}
      transition="top 0.35s cubic-bezier(0.16, 1, 0.3, 1), left 0.35s cubic-bezier(0.16, 1, 0.3, 1), right 0.35s cubic-bezier(0.16, 1, 0.3, 1), border-radius 0.3s ease, background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease"
    >
      <Container maxW="container.xl" px={{ base: 5, md: 8 }}>
        <Flex h={{ base: scrolled ? '58px' : '64px', md: scrolled ? '68px' : '80px' }} align="center" justify="space-between" transition="height 0.3s ease">
          {/* Logo + wordmark */}
          <Box as={RouterLink} to="/" aria-label="Blue Lagoon & Partners home" display="flex" alignItems="center">
            <Image
              src="/images/BLP.png"
              alt="Blue Lagoon & Partners"
              h={{ base: '32px', md: '40px' }}
              w="auto"
              objectFit="contain"
            />
          </Box>

          {/* Desktop nav */}
          <HStack as="nav" spacing={8} display={{ base: 'none', lg: 'flex' }}>
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                isActive={location.pathname === link.to}
              >
                {link.label}
              </NavLink>
            ))}
          </HStack>

          {/* Desktop CTAs */}
          <HStack spacing={3} display={{ base: 'none', md: 'flex' }}>
            <Button as={RouterLink} to="/login" variant="outline" size="sm" h="40px" px={5} fontSize="sm">
              Log In
            </Button>
            <Button as={RouterLink} to="/register" variant="primary" size="sm" h="40px" px={5} fontSize="sm">
              Get Started
            </Button>
          </HStack>

          {/* Mobile hamburger */}
          <IconButton
            aria-label="Open menu"
            icon={<HamburgerIcon />}
            display={{ base: 'flex', lg: 'none' }}
            variant="ghost"
            color="#D4A85F"
            onClick={onOpen}
            ml={2}
          />
        </Flex>
      </Container>

      {/* Mobile drawer */}
      <Drawer placement="right" isOpen={isOpen} onClose={onClose} size={{ base: 'full', sm: 'sm' }}>
        <DrawerOverlay bg="rgba(7,19,38,0.72)" backdropFilter="blur(10px)" />
        <DrawerContent
          bg="#0A1B34"
          color="#F5F7FA"
          backgroundImage="radial-gradient(circle at top right, rgba(212,168,95,0.10) 0%, transparent 55%), radial-gradient(circle at bottom left, rgba(212,168,95,0.05) 0%, transparent 50%)"
          borderLeft="1px solid"
          borderColor="rgba(212,168,95,0.18)"
        >
          <DrawerCloseButton
            color="#D4A85F"
            top={5}
            right={5}
            size="lg"
            borderRadius="full"
            border="1px solid"
            borderColor="rgba(212,168,95,0.25)"
            _hover={{ bg: 'rgba(212,168,95,0.10)', borderColor: 'rgba(212,168,95,0.45)' }}
            transition="all 0.2s ease"
          />

          {/* Brand header */}
          <Box px={6} pt={6} pb={5} borderBottom="1px solid" borderColor="rgba(255,255,255,0.06)">
            <Image
              src="/images/BLP.png"
              alt="Blue Lagoon & Partners"
              h="40px"
              w="auto"
              objectFit="contain"
            />
            <Text mt={4} fontSize="xs" color="#9AA7B8" letterSpacing="0.18em" textTransform="uppercase" fontWeight={500}>
              Navigation
            </Text>
          </Box>

          <DrawerBody px={4} py={5}>
            <VStack align="stretch" spacing={1}>
              {NAV_LINKS.map((link) => {
                const isActive = location.pathname === link.to;
                const Icon = link.icon;
                return (
                  <HStack
                    as={RouterLink}
                    key={link.to}
                    to={link.to}
                    spacing={3}
                    px={4}
                    py={3.5}
                    borderRadius="10px"
                    position="relative"
                    bg={isActive ? 'rgba(212,168,95,0.10)' : 'transparent'}
                    color={isActive ? '#E0B873' : '#D6DCE5'}
                    fontFamily="body"
                    fontWeight={isActive ? 600 : 500}
                    fontSize="md"
                    letterSpacing="0.005em"
                    transition="all 0.22s ease"
                    _hover={{ bg: 'rgba(212,168,95,0.08)', color: '#E0B873', transform: 'translateX(3px)' }}
                    _before={isActive ? {
                      content: '""',
                      position: 'absolute',
                      left: '0',
                      top: '25%',
                      bottom: '25%',
                      w: '3px',
                      bg: 'linear-gradient(180deg, #D4A85F 0%, #E0B873 100%)',
                      borderRadius: '0 3px 3px 0',
                    } : {}}
                  >
                    <Box
                      w="36px"
                      h="36px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="8px"
                      bg={isActive ? 'rgba(212,168,95,0.16)' : 'rgba(255,255,255,0.04)'}
                      border="1px solid"
                      borderColor={isActive ? 'rgba(212,168,95,0.30)' : 'rgba(255,255,255,0.06)'}
                      transition="all 0.22s ease"
                    >
                      <Icon size={17} strokeWidth={1.7} color={isActive ? '#E0B873' : '#9AA7B8'} />
                    </Box>
                    <Text flex="1">{link.label}</Text>
                    <ArrowUpRight size={15} strokeWidth={1.7} color={isActive ? '#E0B873' : 'rgba(154,167,184,0.5)'} />
                  </HStack>
                );
              })}
            </VStack>

            <Box mt={7}>
              <Text fontSize="xs" color="#9AA7B8" letterSpacing="0.18em" textTransform="uppercase" fontWeight={500} px={4} mb={3}>
                Account
              </Text>
              <HStack spacing={3} px={1}>
                <Button as={RouterLink} to="/login" variant="outline" h="46px" fontSize="sm" flex="1">
                  Log In
                </Button>
                <Button as={RouterLink} to="/register" variant="primary" h="46px" fontSize="sm" flex="1">
                  Get Started
                </Button>
              </HStack>
            </Box>

            <Divider my={6} borderColor="rgba(255,255,255,0.06)" />

            <Box px={4}>
              <Text fontSize="xs" color="#9AA7B8" letterSpacing="0.18em" textTransform="uppercase" fontWeight={500} mb={3}>
                Get in Touch
              </Text>
              <VStack align="stretch" spacing={2.5}>
                <HStack
                  as="a"
                  href="mailto:info@theblpartners.com"
                  spacing={2.5}
                  color="#D6DCE5"
                  fontSize="sm"
                  _hover={{ color: '#E0B873' }}
                  transition="color 0.2s ease"
                >
                  <Mail size={14} strokeWidth={1.7} />
                  <Text>info@theblpartners.com</Text>
                </HStack>
                <HStack spacing={2.5} color="#9AA7B8" fontSize="sm">
                  <Phone size={14} strokeWidth={1.7} />
                  <Text>Dubai · Business Bay</Text>
                </HStack>
              </VStack>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;
